"use client";

import { useEffect, useState } from "react";
import TextLink from "../atoms/TextLink";
import { extractPrivacyPolicyHref, type SiteConfigPayload } from "@/lib/site-config-client";

/**
 * @component PrivacyPolicyLink
 * @description Renders the "Privacy Policy" footer link on auth screens.
 * Fetches the link href from `/api/site-config`. When a URL is configured,
 * the link opens in a new tab. When no URL is configured, it renders as
 * disabled text (not clickable).
 *
 * Source priority:
 *  1. `navigation.footer[].href` where label === "Privacy Policy"
 *  2. Top-level `privacyPolicyUrl` (backward-compatible fallback)
 * @returns A TextLink that opens in a new tab, or disabled text when no URL is configured.
 */
export default function PrivacyPolicyLink() {
  const [href, setHref] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const r = await fetch("/api/site-config");
        const json = (await r.json()) as { data: SiteConfigPayload | null };
        if (isMounted) setHref(extractPrivacyPolicyHref(json.data));
      } catch {
        if (isMounted) setHref(null);
      }
    }

    void load();
    return () => {
      isMounted = false;
    };
  }, []);

  if (href === undefined) return null;

  if (!href) {
    return (
      <p className="text-body-semibold text-muted-text cursor-default">
        Privacy Policy
      </p>
    );
  }

  return (
    <TextLink
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-body-semibold text-charcoal hover:text-muted-text transition-colors underline"
    >
      Privacy Policy
    </TextLink>
  );
}
